// DemoPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  Calendar,
  User,
  Mail,
  Building2,
  Phone,
  Shield,
  Zap,
  Users,
  TrendingUp,
} from 'lucide-react';
import api from '../api/api';
import styles from './DemoPage.module.css';
import logo from '../assets/images/zone_icon.png';

// ─────────────────────────────────────────────────────────────
// DONNÉES STATIQUES
// ─────────────────────────────────────────────────────────────
const BENEFITS = [
  {
    Icon: Zap,
    title: 'Analyse IA instantanée',
    desc: 'Traitez des centaines de CV en quelques secondes.',
  },
  {
    Icon: TrendingUp,
    title: 'Matching précis',
    desc: 'Score de pertinence entre CV et offres d\'emploi.',
  },
  {
    Icon: Users,
    title: 'Collaboration fluide',
    desc: 'Partagez et annotez en équipe en temps réel.',
  },
  {
    Icon: Shield,
    title: 'Données sécurisées',
    desc: 'Conformité RGPD et hébergement souverain.',
  },
];

const FIELDS = [
  {
    id: 'prenom',
    label: 'Prénom',
    type: 'text',
    placeholder: 'Jean',
    Icon: User,
    required: true,
    half: true,
  },
  {
    id: 'nom',
    label: 'Nom',
    type: 'text',
    placeholder: 'Dupont',
    Icon: User,
    required: true,
    half: true,
  },
  {
    id: 'email',
    label: 'Email professionnel',
    type: 'email',
    placeholder: 'jean.dupont@entreprise.com',
    Icon: Mail,
    required: true,
    half: false,
  },
  {
    id: 'entreprise',
    label: 'Entreprise',
    type: 'text',
    placeholder: 'Nom de votre société',
    Icon: Building2,
    required: true,
    half: false,
  },
  {
    id: 'telephone',
    label: 'Téléphone',
    type: 'tel',
    placeholder: '+33 1 23 45 67 89',
    Icon: Phone,
    required: false,
    half: false,
  },
];

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const DemoPage = () => {
  const [formData, setFormData] = useState({
    prenom: '', nom: '', email: '', entreprise: '', telephone: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [focused, setFocused] = useState('');

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { prenom, nom, email, entreprise } = formData;

    if (!prenom || !nom || !email || !entreprise) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Veuillez saisir une adresse email valide.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { data } = await api.post('/demo', formData);
      setSuccess(
        data.message ||
        'Votre demande de démo a bien été envoyée ! Notre équipe vous contactera dans les plus brefs délais.'
      );
      setFormData({ prenom: '', nom: '', email: '', entreprise: '', telephone: '' });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        "Une erreur est survenue lors de l'envoi. Veuillez réessayer."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* ── Décors de fond ── */}
      <div className={styles.bgOrb1} aria-hidden="true" />
      <div className={styles.bgOrb2} aria-hidden="true" />
      <div className={styles.bgGrid}  aria-hidden="true" />

      <div className={styles.layout}>

        {/* ════════════════ PANNEAU GAUCHE ════════════════ */}
        <aside className={styles.aside}>
          <div className={styles.asideInner}>

            {/* Logo */}
            <Link to="/" className={styles.logoLink} aria-label="Accueil CVtoSkill">
              <img src={logo} alt="CVtoSkill" className={styles.logo} />
              <span className={styles.logoName}>CVtoSkill</span>
            </Link>

            {/* Accroche */}
            <div className={styles.asideHero}>
              <div className={styles.asideBadge}>
                <Sparkles size={13} strokeWidth={2.5} />
                Plateforme RH intelligente
              </div>
              <h2 className={styles.asideTitle}>
                Transformez votre <span className={styles.asideTitleAccent}>recrutement</span>
                {' '}avec l'IA
              </h2>
              <p className={styles.asideDesc}>
                Rejoignez les équipes RH qui gagnent du temps et améliorent
                la qualité de leurs recrutements grâce à CVtoSkill.
              </p>
            </div>

            {/* Bénéfices */}
            <ul className={styles.benefits}>
              {BENEFITS.map(({ Icon, title, desc }) => (
                <li key={title} className={styles.benefit}>
                  <span className={styles.benefitIcon}>
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <div>
                    <strong className={styles.benefitTitle}>{title}</strong>
                    <p className={styles.benefitDesc}>{desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Social proof */}
            <div className={styles.socialProof}>
              <div className={styles.avatarStack} aria-hidden="true">
                {['M', 'S', 'A', 'L'].map((l) => (
                  <span key={l} className={styles.avatar}>{l}</span>
                ))}
              </div>
              <p className={styles.socialText}>
                <strong>+200 équipes RH</strong> nous font confiance
              </p>
            </div>
          </div>
        </aside>

        {/* ════════════════ PANNEAU DROIT (Formulaire) ════════════════ */}
        <main className={styles.formPanel}>
          <div className={styles.formCard}>

            {/* En-tête */}
            <div className={styles.formHeader}>
              <div className={styles.formHeaderIcon}>
                <Calendar size={22} strokeWidth={1.75} />
              </div>
              <div>
                <h1 className={styles.formTitle}>Réserver une démonstration</h1>
                <p className={styles.formSubtitle}>
                  Gratuit · Sans engagement · Réponse sous 24 h
                </p>
              </div>
            </div>

            {/* ── État succès ── */}
            {success ? (
              <div className={styles.successState}>
                <div className={styles.successIconWrap}>
                  <CheckCircle2 size={48} strokeWidth={1.5} />
                </div>
                <h3 className={styles.successTitle}>Demande envoyée&nbsp;!</h3>
                <p className={styles.successBody}>{success}</p>
                <Link to="/" className={`btn btn-primary ${styles.successCta}`}>
                  Retour à l'accueil
                  <ArrowRight size={16} strokeWidth={2} />
                </Link>
              </div>
            ) : (

              /* ── Formulaire ── */
              <form onSubmit={handleSubmit} className={styles.form} noValidate>

                {/* Erreur */}
                {error && (
                  <div className={styles.alertError} role="alert">
                    <AlertCircle size={18} strokeWidth={2} className={styles.alertIcon} />
                    <span>{error}</span>
                  </div>
                )}

                {/* Champs */}
                <div className={styles.fieldsGrid}>
                  {FIELDS.map(({ id, label, type, placeholder, Icon, required, half }) => (
                    <div
                      key={id}
                      className={`${styles.fieldGroup} ${half ? styles.fieldHalf : ''}`}
                    >
                      <label htmlFor={id} className={styles.label}>
                        {label}
                        {required
                          ? <span className={styles.required} aria-hidden="true">*</span>
                          : <span className={styles.optional}>(optionnel)</span>
                        }
                      </label>

                      <div className={`${styles.inputWrap} ${focused === id ? styles.inputWrapFocused : ''}`}>
                        <span className={styles.inputIcon} aria-hidden="true">
                          <Icon size={16} strokeWidth={1.75} />
                        </span>
                        <input
                          id={id}
                          name={id}
                          type={type}
                          value={formData[id]}
                          onChange={handleChange}
                          onFocus={() => setFocused(id)}
                          onBlur={() => setFocused('')}
                          placeholder={placeholder}
                          required={required}
                          autoComplete={id === 'email' ? 'email' : id === 'telephone' ? 'tel' : 'off'}
                          className={styles.input}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bouton */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={styles.submitBtn}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} strokeWidth={2} className={styles.spinIcon} />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      <Calendar size={18} strokeWidth={2} />
                      Planifier ma démonstration
                      <ArrowRight size={16} strokeWidth={2} className={styles.btnArrow} />
                    </>
                  )}
                </button>

                {/* Note confidentialité */}
                <p className={styles.privacyNote}>
                  <Shield size={13} strokeWidth={2} />
                  Vos données sont protégées et ne seront jamais revendues.
                </p>
              </form>
            )}
          </div>

          {/* Lien retour */}
          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={15} strokeWidth={2} />
            Retour à l'accueil
          </Link>
        </main>
      </div>
    </div>
  );
};

export default DemoPage;